import React, { useState } from 'react'
const { StyleSheet } = XPReactNative
//import Modal from 'react-native-modal'
import { Controller } from 'react-hook-form'
import {
  XStack,
  YStack,
  SimpleText,
  View,
  Image,
  ScrollView,
  ActionButton,
  CustomDatePickerControlled,
  useWindowDimensions,
  XPDatePicker,
  XPModal,
  XPReactNative,
  ActiveButton
} from '@my/ui'
//import DateTimePickerModal from 'react-native-modal-datetime-picker'
import images from '../../assets/images/index'
import { color, formatDate, getImageColorStyle } from '@my/util'
import { CircleX } from '@tamagui/lucide-icons-2'

interface FinancialGoalsCoreProps {
  control: any
  handleSubmit: (callback: (data: any) => void) => () => void
  renderTextInput: (
    name: string,
    placeholder: string,
    rules?: any,
    additionalProps?: any
  ) => JSX.Element
  renderPriorityButtons: () => JSX.Element
  handleSubmitForm: (data) => void
  goalType?: string
  setValue?: any
  hideActionButtons?: boolean
  onClose?: () => void
  labels?: {
    header: string;
    name?: string;
    amount?: string;
    date?: string;
    priorityLevel?: string;
    description?: string;
  };
  hideHeader?: boolean;
}

// Interface for props
interface FinancialGoalsModalProps extends FinancialGoalsCoreProps {
  modalVisible: boolean
  onClose: () => void
}

// Date range mappings
const ranges = {
  'Short term': { start: 0, end: 3 },
  'Mid term': { start: 3, end: 5 },
  'Long term': { start: 5, end: 100 },
}

export const FinancialGoalsCore: React.FC<FinancialGoalsCoreProps> = ({
  control,
  handleSubmit,
  renderTextInput,
  renderPriorityButtons,
  handleSubmitForm,
  goalType,
  hideActionButtons,
  onClose,
  labels,
  hideHeader
}) => {
  const today = new Date()
  const todayDate = today.toLocaleDateString()
  const [isDatePickerVisible, setDatePickerVisibility] = useState(false)

  const showDatePicker = () => {
    setDatePickerVisibility(true)
  }
  const hideDatePicker = () => {
    setDatePickerVisibility(false)
  }

  const handleConfirm = (date) => {
    console.warn('A date has been picked: ', date)
    hideDatePicker()
  }

  // Compute min and max dates
  const { minDate, maxDate } = React.useMemo(() => {
    if (goalType && ranges[goalType]) {
      const { start, end } = ranges[goalType]
      const min = new Date(today.valueOf())
      const max = new Date(today.valueOf())
      min.setFullYear(today.getFullYear() + start)
      max.setFullYear(today.getFullYear() + end)
      //console.log(JSON.stringify({min,max}))
      return { minDate: min, maxDate: max }
    }
    return {
      minDate: new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1),
      maxDate: new Date(today.getFullYear() + 100, today.getMonth(), today.getDate()),
    }
  }, [goalType])

  return <YStack
            bg={color.white}
            paddingVertical={20}
            alignItems={'center'}
            minWidth={260}
            width={'100%'}
            borderRadius={10}
            /* $platform-web={{ width: width < 700 ? '100%' : '60%' }} */
          >
            {/* Header */}
            {!!hideActionButtons ? null : <ActionButton
              unstyled
              circular
              onPress={onClose}
              style={{ position: 'absolute', top: 2, right: 2 }}
              hoverStyle={{backgroundColor: 'rgba(188, 188, 188, 0.66)'}}
              pressStyle={{backgroundColor: 'rgba(188, 188, 188, 0.66)'}}
            >
              <CircleX />
            </ActionButton>}
            {hideHeader ? null : <XStack style={styles.headerContainer}>
              <View style={styles.headerTitleContainer}>
                <SimpleText style={styles.headerTitle}>{labels?.header || 'Add Goals'}</SimpleText>
              </View>
            </XStack>}
            <YStack
              //$platform-web={{ width: '90%' }}
              $platform-native={{ maxWidth: 700 }}
              gap={15}
              width={'100%'}
            >
              {/* Goal Name Input */}
              <YStack style={styles.inputContainer}>
                <SimpleText style={styles.label}>{labels?.name || 'Goal Name'}</SimpleText>
                {renderTextInput('goalName', 'Enter Goal', { required: 'Goal name is required' })}
              </YStack>

              {/* Target Amount Input */}
              <YStack style={styles.inputContainer}>
                <SimpleText style={styles.label}>{labels?.amount || 'Target Amount'}</SimpleText>
                {renderTextInput(
                  'targetAmount',
                  'Amount',
                  { required: 'Target amount is required' },
                  { keyboardType: 'number-pad' }
                )}
              </YStack>

              {/* Priority Level */}
              <YStack style={styles.inputContainer} gap={10}>
                <SimpleText style={styles.label}>{labels?.priorityLevel || 'Priority Level'}</SimpleText>
                {renderPriorityButtons()}
              </YStack>

              {/* Target Date Picker */}
              <YStack style={styles.inputContainer} gap={10}>
                <SimpleText style={styles.label}>{labels?.date || 'Target Date'}</SimpleText>
                <Controller
                  name="timelineTimestamp"
                  control={control}
                  rules={{ required: 'Target date is required' }}
                  render={({ field: { onChange, value, name }, fieldState: { error } }) => (
                    <>
                      <XPDatePicker
                        onChange={(date) => onChange(date)}
                          name="timelineTimestamp"
                        value={value}
                          //placeHolder="Select Date"
                          minDate={minDate}
                          maxDate={maxDate}
                        //icon={images.AdvisorGender}
                        showIcon={false}
                        tintColor={'rgb(133, 193, 157)'}
                        color={color.black}
                        onPress={showDatePicker}
                        borderColor={error ? color.lightRed : 'rgb(224, 225, 230)'}
                        isVisible={isDatePickerVisible}
                        onConfirm={handleConfirm}
                        onCancel={hideDatePicker}
                        actionButtonProps={{
                          unstyled: true,
                          width: '100%',
                          height: 40,
                          //borderWidth: 1,
                          alignItems: 'center',
                          paddingHorizontal: 15,
                          flexDirection: 'row',
                          //borderRadius: 4
                        }}
                        nativeExtraContent={<Image
                          style={{ height: 20, width: 20, ...getImageColorStyle(error ? color.lightRed : color.black) }}
                          src={images.AdvisorGender}
                          //tintColor={error ? color.lightRed : color.black}
                          resizeMode="contain"
                        />}
                        webNative
                        simpleTextProps={{color: error ? color.lightRed : color.black}}
                      />
                    </>
                  )}
                />
              </YStack>

              {/* Description */}
              <YStack style={styles.inputContainer}>
                <SimpleText style={styles.label}>{labels?.description || 'Description (optional)'}</SimpleText>
                {renderTextInput('description', 'Description')}
              </YStack>

              {/* Button Group */}
              {!!hideActionButtons ? null : <View style={styles.buttonGroupContainer}>
                <XStack style={styles.buttonGroup}>
                  <ActiveButton style={{width: 80, padding: 0}} ButtonTextProps={{children: 'Save'}} onPress={handleSubmit(handleSubmitForm)} />
                  <ActiveButton style={{width: 90, padding: 0}} ButtonTextProps={{children: 'Cancel'}} variant='outlined' onPress={onClose} />
                </XStack>
              </View>}
            </YStack>
          </YStack>
}

export const FinancialGoalsModal: React.FC<FinancialGoalsModalProps> = (props) => {
  const { modalVisible, ...restProps } = props;
  const onClose = props.onClose;

  return (
    <XPModal open={modalVisible} onBackgroundPress={onClose} otherProps={{style:styles.modalContainer}} nativeModalType={'library'} webContentProps={{backgroundColor: 'white'}}>
      <View zIndex={1} alignItems='center' justifyContent='center'>
          <FinancialGoalsCore {...restProps} />
      </View>
    </XPModal>
  )
}

const styles = StyleSheet.create({
  modalContainer: { flex: 1 },
  headerContainer: { width: '100%', marginBottom: 20 },
  headerTitleContainer: { width: '100%', alignItems: 'center' },
  headerTitle: { fontWeight: '700', fontSize: 20, color: color.tzaGreen },
  closeButton: { position: 'absolute', right: 5, top: 5 },
  closeIcon: { height: 17, width: 17 },
  inputContainer: { paddingHorizontal: 10 },
  label: { fontSize: 16, fontWeight: '700', color: color.grey },
  datePickerButton: { borderBottomWidth: 2, borderBottomColor: '#85C19D' },
  dateText: { fontWeight: '600', fontSize: 17 },
  dateTimePicker: {
    alignSelf: 'flex-start',
    backgroundColor: 'white',
    opacity: 0.5,
    color: color.black,
    flex: 1,
  },
  buttonGroupContainer: { width: '100%', marginTop: 20 },
  buttonGroup: { gap: 10, paddingHorizontal: 10, justifyContent: 'flex-end', width: '100%' },
  saveButton: { backgroundColor: '#85C19D', color: '#FFF', fontSize: 17 },
  cancelButton: { color: '#848484', borderColor: '#848484', fontSize: 17 },
})
